import type { ItemInstance, ItemTemplate } from '@shefira/shared';

import type { CssAtlas } from '../assets/dom-atlas.js';
import { closeButton } from './hud.js';
import { addLevelBadge, paintItemIcon } from './item-icon.js';
import { uiFormat, uiText } from './text.js';
import './shop-modals.css';
import './destroy-item.css';

/**
 * The step before a bag item is gone (ADR 0084): its icon, its name, and for
 * a stack how many go. Only asks: the server checks the item, the count and
 * what a quest lent, and answers with the bag or a refusal.
 *
 * Resolves the count to destroy, or null if the player backed out.
 */
export function confirmItemDestruction(
  template: ItemTemplate,
  instance: ItemInstance,
  icons: CssAtlas | null,
): Promise<number | null> {
  return new Promise((resolve) => {
    const root = document.createElement('div');
    root.className = 'shop-modal';
    root.setAttribute('role', 'dialog');
    root.setAttribute('aria-modal', 'true');
    const name = uiText(template.name);
    root.setAttribute('aria-label', uiFormat('Détruire {name}', { name }));

    const frame = document.createElement('section');
    frame.className = 'shop-modal__window';
    const title = document.createElement('h2');
    title.className = 'shop-modal__title';
    title.textContent = uiFormat('Détruire {name} ?', { name });

    const shown = document.createElement('div');
    shown.className = 'shop-modal__items';
    const slot = document.createElement('div');
    slot.className = 'slot';
    const icon = document.createElement('div');
    icon.className = `item item--${template.rarity}`;
    paintItemIcon(icon, template, icons, instance.templateId);
    addLevelBadge(icon, instance.upgradeLevel);
    slot.append(icon);
    shown.append(slot);

    const lead = document.createElement('p');
    lead.className = 'shop-modal__hint';
    lead.textContent = uiText('Un objet détruit disparaît pour toujours : personne ne pourra te le rendre.');

    const stack = instance.quantity;
    let count = stack;
    const counter = document.createElement('div');
    counter.className = 'destroy-count';
    const value = document.createElement('span');
    value.className = 'destroy-count__value';
    value.setAttribute('aria-live', 'polite');
    const step = (label: string, aria: string, run: () => void): HTMLButtonElement => {
      const button = document.createElement('button');
      button.type = 'button';
      button.className = 'shop-modal__button destroy-count__step';
      button.textContent = label;
      button.setAttribute('aria-label', uiText(aria));
      button.addEventListener('click', () => {
        run();
        refresh();
      });
      return button;
    };
    const less = step('−', 'Un de moins', () => {
      count = Math.max(1, count - 1);
    });
    const more = step('+', 'Un de plus', () => {
      count = Math.min(stack, count + 1);
    });
    const all = step(uiText('Tout'), 'Toute la pile', () => {
      count = stack;
    });
    counter.append(less, value, more, all);

    const actions = document.createElement('div');
    actions.className = 'shop-modal__actions';
    const cancel = document.createElement('button');
    cancel.type = 'button';
    cancel.className = 'shop-modal__button';
    cancel.textContent = uiText('Annuler');
    const confirm = document.createElement('button');
    confirm.type = 'button';
    confirm.className = 'shop-modal__button shop-modal__button--danger';
    actions.append(cancel, confirm);

    function refresh(): void {
      value.textContent = uiFormat('{count} / {stack}', { count, stack });
      less.disabled = count <= 1;
      more.disabled = count >= stack;
      all.disabled = count >= stack;
      confirm.textContent =
        stack > 1 ? uiFormat('Détruire ×{count}', { count }) : uiText('Détruire');
    }
    refresh();

    const finish = (destroyed: number | null): void => {
      window.removeEventListener('keydown', onKeyDown, true);
      root.remove();
      resolve(destroyed);
    };
    const onKeyDown = (event: KeyboardEvent): void => {
      // The game and the inventory behind must not read the keys as shortcuts.
      event.stopPropagation();
      if (event.key === 'Escape') finish(null);
    };
    cancel.addEventListener('click', () => {
      finish(null);
    });
    confirm.addEventListener('click', () => {
      finish(count);
    });
    root.addEventListener('click', (event) => {
      if (event.target === root) finish(null);
    });

    frame.append(title, shown, lead);
    if (stack > 1) frame.append(counter);
    frame.append(
      actions,
      closeButton('Annuler (Échap)', () => {
        finish(null);
      }),
    );
    root.append(frame);
    document.body.append(root);
    window.addEventListener('keydown', onKeyDown, true);
    // Focus on « Annuler »: a stray Enter keeps the item.
    cancel.focus();
  });
}
